import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import {
  useNotifications,
  useMarkAllAsRead,
  useMarkAsRead,
  useUnreadCount,
} from '../../hooks/use-notifications.ts';
import type { Notification } from '../../api/types.ts';
import { formatRelativeTime, notificationIcon } from './notification-display.ts';

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: unread } = useUnreadCount();
  const unreadCount = unread?.count ?? 0;
  const { data: list } = useNotifications({ page: 1, pageSize: 8 });
  const notifications = list?.data ?? [];
  const markRead = useMarkAsRead();
  const markAll = useMarkAllAsRead();

  useEffect(() => {
    if (!open) return;
    function handlePointer(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const openItem = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) markRead.mutate(notification.id);
      setOpen(false);
      if (notification.link) navigate(notification.link);
    },
    [markRead, navigate],
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="relative rounded-md p-1.5 text-muted-foreground transition-colors duration-[var(--motion-fast)] hover:bg-accent hover:text-foreground"
        aria-label={t('notifications.bellAria')}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none text-primary-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-ui font-semibold text-foreground">{t('notifications.title')}</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="text-caption font-medium text-brand-text hover:underline disabled:opacity-50"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-caption text-muted-foreground">
                {t('notifications.empty')}
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = notificationIcon(notification.type);
                return (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => openItem(notification)}
                    className={`flex w-full gap-3 border-b border-border/50 px-4 py-3 text-left transition-colors duration-[var(--motion-fast)] hover:bg-muted/40 ${
                      notification.isRead ? '' : 'bg-brand-tint/60'
                    }`}
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {!notification.isRead && (
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                        )}
                        <span className="truncate text-ui font-medium text-foreground">{notification.title}</span>
                      </div>
                      {notification.message && (
                        <p className="mt-0.5 line-clamp-2 text-caption text-muted-foreground">{notification.message}</p>
                      )}
                      <span className="mt-1 block text-[10px] tabular-nums text-muted-foreground">
                        {formatRelativeTime(notification.createdAt)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
            className="block w-full border-t border-border px-4 py-2.5 text-center text-caption font-medium text-brand-text transition-colors duration-[var(--motion-fast)] hover:bg-muted/40"
          >
            {t('notifications.viewAll')}
          </button>
        </div>
      )}
    </div>
  );
}
